import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GuesteriaPartnersTable } from "@/components/pages/guesteria-partners/components/GuesteriaPartnersTable";
import { GuesteriaPartnersFiltersPopover } from "@/components/pages/guesteria-partners/components/GuesteriaPartnersFiltersPopover";
import { GuesteriaServicesTable } from "@/components/pages/guesteria-partners/components/GuesteriaServicesTable";
import { GuesteriaServicesFiltersPopover } from "@/components/pages/guesteria-partners/components/GuesteriaServicesFiltersPopover";
import {
  DEFAULT_GUESTERIA_PARTNERS_FILTERS,
  filterGuesteriaPartnerRows,
  type GuesteriaPartnersFiltersState,
} from "@/components/pages/guesteria-partners/utils/guesteriaPartnersFilters";
import {
  DEFAULT_GUESTERIA_SERVICES_FILTERS,
  toPartnerServicesFilters,
  type GuesteriaServicesFiltersState,
} from "@/components/pages/guesteria-partners/utils/guesteriaServicesFilters";
import {
  GUESTERIA_SERVICES_PAGE_SIZE,
  clampPage,
  getTotalPages,
} from "@/components/pages/guesteria-partners/utils/guesteriaServicesPagination";
import {
  DEFAULT_SERVICES_SORT,
  getNextServicesSort,
  getPageAfterSortChange,
  type ServicesSortState,
} from "@/components/pages/guesteria-partners/utils/guesteriaServicesSorting";
import {
  mapPartnerSettingsToRow,
  providerSlugFromSettings,
} from "@/components/pages/guesteria-partners/utils/mapPartnerSettingsToGuesteria";
import {
  useGetPartnerSettings,
  useUpdatePartnerSettings,
  useGetPartnerServices,
  useGetPartnerServicesFilterOptions,
} from "@/components/pages/partner-integration-settings/hooks/partner-settings.hooks";
import { usePartnerRegionFlag } from "@/components/pages/guesteria-partners/hooks/usePartnerRegionFlag";
import { GuesteriaActivitiesForGuestsTab } from "@/components/pages/guesteria-partners/components/GuesteriaActivitiesForGuestsTab";
import { useHotel } from "@/hooks/useHotel";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { Role } from "@/types/user";
import { BillingPageState } from "@/components/pages/billing-settings/components/BillingPageState";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Roles that get the "Activities for guests" tab (task 0027) — deliberately
// excludes plain STAFF. The route itself only gates on
// `canUseGuesteriaPartners` (App.tsx), so STAFF already reaches this page
// via the Providers/Services tabs; this tab is gated in-component instead
// of pulling that guard down onto the whole page.
const CURATION_TAB_ROLES: string[] = [
  Role.HOTEL_ADMIN,
  Role.FRONT_DESK,
  Role.TENANT_ADMIN,
];

export const GuesteriaPartnersPage = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { hotelId } = useHotel();
  const { userProfile } = useAuthenticatedUser();
  const canManageCuration = Boolean(
    userProfile?.role && CURATION_TAB_ROLES.includes(userProfile.role),
  );
  const { data: settings, isLoading, isError } = useGetPartnerSettings();
  const { data: regionFlagEmoji } = usePartnerRegionFlag(
    hotelId,
    Boolean(
      settings?.destinationName?.trim() && !settings?.destination?.flagEmoji,
    ),
  );
  const updatePartnerSettings = useUpdatePartnerSettings();

  // Providers tab
  const [filters, setFilters] = useState<GuesteriaPartnersFiltersState>(
    DEFAULT_GUESTERIA_PARTNERS_FILTERS,
  );

  // Services tab
  const [servicesFilters, setServicesFilters] =
    useState<GuesteriaServicesFiltersState>(DEFAULT_GUESTERIA_SERVICES_FILTERS);
  const [servicesPage, setServicesPage] = useState(1);
  const [servicesSort, setServicesSort] = useState<ServicesSortState>(
    DEFAULT_SERVICES_SORT,
  );
  const handleServicesSortChange = useCallback(
    (field: string) => {
      const next = getNextServicesSort(servicesSort, field);
      setServicesPage((page) =>
        getPageAfterSortChange(servicesSort, next, page),
      );
      setServicesSort(next);
    },
    [servicesSort],
  );
  const { data: filterOptions } = useGetPartnerServicesFilterOptions();
  const partnerServicesFilters = useMemo(
    () => ({
      ...toPartnerServicesFilters(servicesFilters, filterOptions?.categories),
      page: servicesPage,
      limit: GUESTERIA_SERVICES_PAGE_SIZE,
    }),
    [servicesFilters, servicesPage, filterOptions?.categories],
  );
  const handleServicesFiltersApply = useCallback(
    (next: GuesteriaServicesFiltersState) => {
      setServicesFilters(next);
      setServicesPage(1);
    },
    [],
  );
  const { data: servicesData, isLoading: servicesLoading } =
    useGetPartnerServices(partnerServicesFilters);
  const servicesTotalPages = getTotalPages(
    servicesData?.total,
    GUESTERIA_SERVICES_PAGE_SIZE,
  );

  const filteredPartners = useMemo(() => {
    if (!settings) {
      return [];
    }
    const rows = [mapPartnerSettingsToRow(settings, t, regionFlagEmoji)];
    return filterGuesteriaPartnerRows(rows, filters);
  }, [settings, filters, t, regionFlagEmoji]);

  const handlePartnerEnabledChange = useCallback(
    (id: string, enabled: boolean) => {
      if (!settings) {
        return;
      }
      if (providerSlugFromSettings() !== id) {
        return;
      }
      updatePartnerSettings.mutate({ enabled });
    },
    [settings, updatePartnerSettings],
  );

  if (!hotelId) {
    return <BillingPageState text={t("partners.hotelNotSelected")} />;
  }

  if (isLoading) {
    return <BillingPageState text={t("partners.loading")} />;
  }

  if (isError) {
    return <BillingPageState text={t("partners.loadingError")} tone="error" />;
  }

  return (
    <div className="flex w-full flex-col gap-6 p-6">
      <Tabs defaultValue="providers" className="w-full">
        <TabsList
          className={
            canManageCuration
              ? "grid w-full max-w-md grid-cols-3"
              : "grid w-full max-w-xs grid-cols-2"
          }
        >
          <TabsTrigger value="providers">
            {t("partners.tabs.providers")}
          </TabsTrigger>
          <TabsTrigger value="services">
            {t("partners.tabs.services")}
          </TabsTrigger>
          {canManageCuration && (
            <TabsTrigger value="activitiesForGuests">
              {t("partners.tabs.activitiesForGuests")}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="providers" className="mt-6 space-y-6">
          <GuesteriaPartnersFiltersPopover
            applied={filters}
            onApply={setFilters}
          />
          <GuesteriaPartnersTable
            partners={filteredPartners}
            onPartnerEnabledChange={handlePartnerEnabledChange}
          />
        </TabsContent>

        <TabsContent value="services" className="mt-6 space-y-6">
          {filterOptions ? (
            <GuesteriaServicesFiltersPopover
              applied={servicesFilters}
              onApply={handleServicesFiltersApply}
              categories={filterOptions.categories}
              priceRange={filterOptions.priceRange}
            />
          ) : null}
          <GuesteriaServicesTable
            services={servicesData?.data ?? []}
            isLoading={servicesLoading}
            sort={servicesSort}
            onSortChange={handleServicesSortChange}
            pagination={{
              currentPage: clampPage(servicesPage, servicesTotalPages),
              totalPages: servicesTotalPages,
              onPageChange: (page) =>
                setServicesPage(clampPage(page, servicesTotalPages)),
            }}
          />
        </TabsContent>

        {canManageCuration && (
          <TabsContent value="activitiesForGuests" className="mt-6 space-y-6">
            <GuesteriaActivitiesForGuestsTab
              hotelId={hotelId}
              cityName={
                settings?.destinationName ||
                settings?.destination?.destinationName ||
                undefined
              }
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
